import { ApiService } from '../ApiService';
import type {
  AnalyticAccount,
  CreateAnalyticAccountPayload,
  UpdateAnalyticAccountPayload,
  AnalyticLine,
  CreateAnalyticLinePayload,
  AnalyticReportFilter,
  AnalyticReportData,
} from '../../../types/analytic-accounting';

/**
 * API endpoints pour le module Comptabilité Analytique
 * 
 * Comptes Analytiques :
 * @see POST /analytic/accounts — Créer un compte analytique
 * @see GET /analytic/accounts — Lister les comptes analytiques
 * @see GET /analytic/accounts/:id — Détail d'un compte
 * @see PUT /analytic/accounts/:id — Mettre à jour un compte
 * @see DELETE /analytic/accounts/:id — Supprimer un compte
 * 
 * Lignes Analytiques :
 * @see POST /analytic/lines — Affecter une ligne d'écriture à un axe analytique
 * @see GET /analytic/lines/journal-line/:journalLineId — Ventilations d'une ligne
 * 
 * Rapport :
 * @see GET /analytic/report — Rapport analytique avec filtres
 */
export const analyticAccountingApi = {
  // ============================================
  // COMPTES ANALYTIQUES
  // ============================================

  /**
   * Lister tous les comptes analytiques
   */
  getAccounts: (params?: { type?: string; isActive?: boolean; search?: string }) =>
    ApiService.get<{ success: boolean; data: AnalyticAccount[] }>('/analytic/accounts', params),

  /**
   * Récupérer un compte analytique par ID
   */
  getAccountById: (id: string) =>
    ApiService.get<{ success: boolean; data: AnalyticAccount }>(`/analytic/accounts/${id}`),

  /**
   * Créer un compte analytique
   */
  createAccount: (data: CreateAnalyticAccountPayload) =>
    ApiService.post<{ success: boolean; data: AnalyticAccount }>('/analytic/accounts', data),

  /**
   * Mettre à jour un compte analytique
   */
  updateAccount: (id: string, data: UpdateAnalyticAccountPayload) =>
    ApiService.put<{ success: boolean; data: AnalyticAccount }>(`/analytic/accounts/${id}`, data),

  /**
   * Supprimer un compte analytique
   */
  deleteAccount: (id: string) =>
    ApiService.delete<{ success: boolean }>(`/analytic/accounts/${id}`),

  // ============================================
  // LIGNES ANALYTIQUES
  // ============================================

  /**
   * Affecter une ligne d'écriture comptable à un axe analytique
   */
  createLine: (data: CreateAnalyticLinePayload) =>
    ApiService.post<{ success: boolean; data: AnalyticLine }>('/analytic/lines', data),

  /**
   * Récupérer toutes les ventilations analytiques d'une ligne d'écriture
   */
  getLinesByJournalLine: (journalLineId: string) =>
    ApiService.get<{ success: boolean; data: AnalyticLine[] }>(`/analytic/lines/journal-line/${journalLineId}`),

  // ============================================
  // RAPPORT ANALYTIQUE
  // ============================================

  /**
   * Obtenir le rapport analytique avec filtres
   */
  getReport: (params: AnalyticReportFilter) =>
    ApiService.get<{ success: boolean; data: AnalyticReportData }>('/analytic/report', params),
};
